import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { useParams } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { useLanguage } from "./LanguageContext";
import { useDataRefresh } from "./liveUpdates/DataRefreshProvider";
import { api } from "../lib/http";

import AromaSection from "./aromasection";
import PriceChangedModal from "./PriceChangeModal";
import AuthExpiredModal from "./AuthExpiredModal";
import AddressPopup from "./AddressPopup";
import MiniRegisterModal from "./MiniRegisterModal";
import Review from "./Review";

const API_BASE_URL = "https://shopaholics.ge/api";

/* =============== THEME =============== */
const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  card: "#ffffff",
  shadow: "0 10px 30px rgba(0,0,0,.08)",
};

/* =============== LOADER =============== */
const spin = keyframes`0%{transform:rotate(0)}100%{transform:rotate(360deg)}`;
const Loader = styled.div`min-height: 60vh; display: grid; place-items: center;`;
const Spinner = styled.div`
  width: 42px; height: 42px;
  border: 4px solid rgba(0,0,0,.08);
  border-top-color: #000;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

/* =============== LAYOUT =============== */
const Page = styled.div`max-width: 1280px; margin: 16px auto 32px; padding: 0 16px;`;
const TitleDesktop = styled.h1`
  font-size: 22px; font-weight: 900; color: ${COLORS.text}; margin: 8px 0 16px;
  @media (max-width: 768px){ display:none; }
`;
const TitleMobile = styled.h1`
  display:none; font-size: 20px; font-weight: 900; color: ${COLORS.text}; margin: 8px 0 12px;
  @media (max-width: 768px){ display:block; }
`;
const Grid = styled.div`
  display: grid; grid-template-columns: 1.1fr 1fr; gap: 16px; align-items: start;
  @media (max-width: 1024px){ grid-template-columns: 1fr; }
`;
const Card = styled.div`
  background: ${COLORS.card}; border: 1px solid ${COLORS.line};
  border-radius: 16px; box-shadow: ${COLORS.shadow};
`;

/* =============== GALLERY =============== */
const GalleryCard = styled(Card)`padding: 16px; display: grid; gap: 12px; align-self: start; height: max-content;`;
const SaleRow = styled.div`display:flex; align-items:center; justify-content:space-between;`;
const blink = keyframes`0%{opacity:1}50%{opacity:.2}100%{opacity:1}`;
const SaleBadge = styled.div`
  background: #ef4444; color: #fff; font-weight: 800; padding: 6px 10px; border-radius: 999px; font-size: 12px;
  box-shadow: 0 6px 16px rgba(239,68,68,.35); animation: ${blink} 1.5s infinite; display: inline-flex; align-items:center; justify-content:center;
`;
const MainImageWrap = styled.div`
  background: ${COLORS.subtle}; border: 1px solid ${COLORS.line}; border-radius: 14px; width: 100%; aspect-ratio: 1 / 1;
  display: grid; justify-items: center; align-items: start; padding-top: 10px; overflow: hidden;
`;
const MainImage = styled.img`width: 80%; height: 80%; object-fit: contain; object-position: center; display: block;`;
const Thumbs = styled.div`
  display: flex; gap: 10px; overflow-x: auto; padding-bottom: 6px;
  scrollbar-width: thin;
  &::-webkit-scrollbar{ height:6px; }
  &::-webkit-scrollbar-track{ background:#f0f0f0; border-radius:10px; }
  &::-webkit-scrollbar-thumb{ background:#c8c8cc; border-radius:10px; }
  img{ width: 56px; height: 56px; object-fit: cover; border-radius: 10px; border: 2px solid transparent; cursor: pointer; flex: 0 0 auto; }
  img.selected, img:hover{ border-color: #000; }
`;

/* =============== DETAILS =============== */
const DetailsCard = styled(Card)`padding: 16px 16px 8px;`;
const Row = styled.div`display:flex; gap:6px; color:${COLORS.text}; margin: 6px 0; strong{ font-weight:900; }`;
const Info = styled.p`color:${COLORS.text}; margin: 6px 0; line-height: 1.5;`;
const Toggle = styled.button`
  width:100%; display:flex; align-items:center; justify-content:space-between;
  padding: 12px 14px; margin-top: 10px; background: #f9f9f9; border: 1px solid ${COLORS.line};
  border-radius: 12px; cursor:pointer; font-weight: 900; color:${COLORS.text};
`;
const ToggleContent = styled.div`
  overflow: hidden; max-height: ${({open}) => open ? "600px" : "0px"}; transition: max-height .35s ease;
  padding: ${({open}) => open ? "10px 2px 2px" : "0 2px"}; p{ color:#6b7280; white-space: pre-wrap; }
`;

/* ML selector */
const MlBlock = styled.div`margin-top: 8px; border: 1px dashed ${COLORS.line}; border-radius: 12px; padding: 10px; max-width: 420px;`;
const MlRow = styled.label`
  display:flex; align-items:center; gap:10px; cursor: ${({disabled})=>disabled?'not-allowed':'pointer'};
  opacity: ${({disabled})=>disabled?.toString()==='true'?'0.5':'1'}; padding: 8px 6px; border-radius: 10px;
  input{ display:none; }
  span.chk{ width: 20px; height: 20px; border:1px solid #6b7280; border-radius: 4px; display:inline-block; position:relative; background:#fff; }
  input:checked + span.chk{ background:#000; border-color:#000; }
  input:checked + span.chk::before{ content:"✔"; color:#fff; font-size:13px; font-weight:900; position:absolute; inset:0; display:grid; place-items:center; }
`;
const PriceWrap = styled.div`
  display:flex; align-items:center; gap:8px; font-weight:800; flex-wrap: wrap;
  .old{ text-decoration: line-through; color:#9ca3af; }
`;
const MlStock = styled.div`margin-left: 30px; color:#6b7280; font-size:12px; font-weight:700;`;

/* =============== ACTIONS / PRICE =============== */
const ActionsCard = styled(Card)`
  margin-top: 16px; padding: 16px; display: grid; gap: 12px; position: sticky; top: 12px;
  @media (max-width: 1024px){ position: static; }
`;
const QtyRow = styled.div`display:flex; align-items:center; gap: 10px;`;
const QtyBadge = styled.div`min-width: 80px; color:${COLORS.text};`;
const QtyField = styled.div`display:flex; align-items:center; gap: 6px;`;
const QtyBtn = styled.button`
  width: 34px; height: 34px; border-radius: 999px; border: 1px solid ${COLORS.line}; background: #fff;
  display:grid; place-items:center; font-size:18px; cursor:pointer; transition: transform .15s ease;
  &:hover{ transform: translateY(-1px); } &:disabled{ opacity:.4; cursor:not-allowed; }
`;
const QtyInput = styled.input`
  width: 42px; height: 34px; border:1px solid ${COLORS.line}; border-radius: 8px; text-align:center; font-size: 14px; padding: 0 4px;
  appearance: textfield; &::-webkit-outer-spin-button,&::-webkit-inner-spin-button{ -webkit-appearance: none; margin:0; }
`;
const Total = styled.div`
  display:flex; align-items:center; justify-content:space-between;
  padding: 10px 12px; border:1px solid ${COLORS.line}; border-radius: 12px; background:#fff;
  strong{ font-size: 18px; }
`;
const Buttons = styled.div`
  display:grid; grid-template-columns: 1fr; gap: 10px;
  .btn{ height: 44px; border-radius: 12px; border:1px solid ${COLORS.line}; font-weight: 900; cursor:pointer; transition: transform .12s ease; }
  .btn:active{ transform: scale(.99); }
  .primary{ background: ${COLORS.text}; color:#fff; }
  .secondary{ background: #fff; color:${COLORS.text}; }
  .or{ text-align:center; color:#6b7280; font-weight:700; }
`;

/* =============== STOCK MODAL =============== */
const StockModalBg = styled.div`position: fixed; inset: 0; background: rgba(0,0,0,.35); z-index: 4000; display: grid; place-items: center;`;
const StockModalCard = styled.div`
  width: min(520px, 92vw); background: #fff; border: 1px solid rgba(0,0,0,.08);
  border-radius: 16px; box-shadow: 0 10px 30px rgba(0,0,0,.08); padding: 18px; text-align: center;
`;

const softPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(17,24,39,.08); transform: scale(1); }
  50%  { box-shadow: 0 0 0 5px rgba(17,24,39,.08); transform: scale(1.006); }
  100% { box-shadow: 0 0 0 0 rgba(17,24,39,.08); transform: scale(1); }
`;

const ColorChip = styled.button`
  padding: 6px 10px; border-radius: 999px; border: 1px solid rgba(0,0,0,.12);
  background: #fff; color: #111827; font-weight: 800; cursor: pointer;
  &[data-active="true"]{ background:#111827; color:#fff; }
  &[data-sale="true"]{ animation: ${softPulse} 1.6s ease-in-out infinite; }
  /* როცა სეილიცაა და არჩეულიცაა — ტექსტი იყოს შავი, ფონიც ნათელი */
  &[data-active="true"][data-sale="true"]{
    background:#000000;
    color:#ffffff;
}
`;

const SizeButtonPulsePatch = styled.div`
  button[data-sale="true"]{ animation: ${softPulse} 1.6s ease-in-out infinite; }
`;

const PayOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 999999;
  display: grid;
  place-items: center;
  padding: 12px;
`;

const PayShell = styled.div`
  width: min(980px, 96vw);
  height: min(92vh, 860px);
  background: #fff;
  border-radius: 18px;
  border: 1px solid rgba(0,0,0,.08);
  box-shadow: 0 18px 50px rgba(0,0,0,.18);
  overflow: hidden;

  display: flex;
  flex-direction: column;

  /* iOS safe-area */
  padding-bottom: env(safe-area-inset-bottom);
`;

const PayHeader = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(0,0,0,.08);
  background: #fff;
`;

const PayTitle = styled.div`
  font-weight: 900;
  color: #111827;
  letter-spacing: .01em;
`;

const PayClose = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 12px;
  border: 1px solid rgba(0,0,0,.08);
  background: #111827;
  color: #fff;
  font-size: 22px;
  font-weight: 900;
  cursor: pointer;
  transition: transform .12s ease, filter .12s ease;
  &:hover { transform: translateY(-1px); filter: brightness(.95); }
`;

const PayHint = styled.div`
  flex: 0 0 auto;
  padding: 10px 14px;
  font-size: 12.5px;
  color: #6b7280;
  font-weight: 800;
  border-bottom: 1px solid rgba(0,0,0,.06);
  background: #fafafa;
`;

const PayFrameWrap = styled.div`
  flex: 1 1 auto;
  min-height: 0; /* 🔥 ეს არის მთავარი რომ iframe არ მოიჭრას flex-ში */
  background: #fff;
`;

const PayFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
`;



function StockModal({ message, onClose }) {
  return (
    <StockModalBg onClick={onClose}>
      <StockModalCard onClick={(e)=>e.stopPropagation()}>
        <h3 style={{margin:"4px 0 8px", fontWeight:900}}>⚠️</h3>
        <p style={{margin:"0 0 12px", color:"#6b7280"}}>{message}</p>
        <button
          onClick={onClose}
          style={{height:40,padding:"0 16px",borderRadius:10,border:"1px solid rgba(0,0,0,.08)",background:"#111827",color:"#fff",fontWeight:800,cursor:"pointer"}}
        >
          OK
        </button>
      </StockModalCard>
    </StockModalBg>
  );
}

function PaymentIframeModal({ url, onClose, language }) {
  const T = (ka, en) => (language === "ka" ? ka : en);

  return (
    <PayOverlay onClick={onClose}>
      <PayShell onClick={(e) => e.stopPropagation()}>
        {/* Header (fixed) */}
        <PayHeader>
          <PayTitle>{T("გადახდის დადასტურება", "Confirm payment")}</PayTitle>
          <PayClose onClick={onClose} aria-label="Close">×</PayClose>
        </PayHeader>

        {/* Optional hint row (small) */}
        <PayHint>
          {T(
            "შეიყვანეთ SMS კოდი და დაასრულეთ გადახდა. თუ არაფერი ჩანს, დააჭირეთ Close და გავხსნით ჩვეულებრივად.",
            "Enter the SMS code to finish. If it’s blank, close and we’ll open it normally."
          )}
        </PayHint>

        {/* Iframe fills remaining height */}
        <PayFrameWrap>
          <PayFrame
            title="payment"
            src={url}
            allow="payment *; clipboard-read *; clipboard-write *"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </PayFrameWrap>
      </PayShell>
    </PayOverlay>
  );
}


/* =============== COMPONENT =============== */
const PerfumeDetail = ({ addToCart, forceProductId, hideActions = false }) => {
  const { version } = useDataRefresh();
  const params = useParams();
  const product_id = forceProductId ?? params.product_id;

  const { t, language } = useLanguage();
  const T = (ka, en) => (language === "ka" ? ka : en);

  const [perfume, setPerfume] = useState(null);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedMilliliters, setSelectedMilliliters] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [totalPrice, setTotalPrice] = useState("0.00");

  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [isNoteOpen, setIsNoteOpen] = useState(false);
  const [isMillilitersOpen, setIsMillilitersOpen] = useState(false);

  const [userId, setUserId] = useState(null);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showAddressPopup, setShowAddressPopup] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [tempUser, setTempUser] = useState(null);
  const [popupAssumeHasPhone, setPopupAssumeHasPhone] = useState(false);
  const [loading, setLoading] = useState(true);

  const descriptionRef = useRef(null);
  const noteRef = useRef(null);
  const millilitersRef = useRef(null);
  const [silentRefreshing, setSilentRefreshing] = useState(false);
  const firstLoadRef = useRef(true);
  const lastSilentRefetch = useRef(0);

  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [stockMessage, setStockMessage] = useState("");

  const [selectedColor, setSelectedColor] = useState(null);     // accessories: color_ka
  const [selectedVariant, setSelectedVariant] = useState(null); // accessories: chosen variant (color+size)

    const [payModalOpen, setPayModalOpen] = useState(false);
const [payUrl, setPayUrl] = useState("");


  const openStockModal = (msg) => {
    setShowAddressPopup(false);
    setStockMessage(msg);
    setStockModalOpen(true);
  };

  const [deliveryFee, setDeliveryFee] = useState(0);
  /* ---------- fetch ---------- */
  const loadPerfume = async ({ withSpinner = false } = {}) => {
    if (withSpinner) setLoading(true);
    try {
      const res = await api.get(`${API_BASE_URL}/get_products.php`);
      const list = Array.isArray(res.data) ? res.data : [];
      const item = list.find(p => p.product_id === product_id);
      if (!item) return;

      const normalized = {
        ...item,
        milliliters: Array.isArray(item.milliliters) ? item.milliliters : [],
        imageUrls: Array.isArray(item.imageUrls) ? item.imageUrls : [],
        accessory_variants: Array.isArray(item.accessory_variants) ? item.accessory_variants : [],
      };

      setPerfume(prev => normalized);
    } catch (e) {
      console.error(e);
    } finally {
      if (withSpinner) setTimeout(() => setLoading(false), 250);
    }
  };


useEffect(() => {
  const fetchSettings = async () => {
    try {
      const res = await api.get(`${API_BASE_URL}/get_settings.php`);

      console.log("SETTINGS RESPONSE:", res.data); // 🔥 DEBUG

      const fee =
        res.data?.delivery_fee ??
        res.data?.data?.delivery_fee ??
        res.data?.settings?.delivery_fee ??
        (Array.isArray(res.data) ? res.data[0]?.delivery_fee : null);

      setDeliveryFee(Number(fee ?? 0));
    } catch (e) {
      console.error("delivery fee fetch error", e);

      // ❗ აქ აღარ დააბრუნო 7 — ეს გატყუებს
      setDeliveryFee(0);
    }
  };

  fetchSettings();
}, []);


  useEffect(() => {
    let alive = true;
    (async () => {
      await loadPerfume({ withSpinner: true });
      if (alive) firstLoadRef.current = false;
    })();
    return () => { alive = false; };
  }, [product_id]);

  useEffect(() => {
    if (firstLoadRef.current) return;
    const now = Date.now();
    if (now - lastSilentRefetch.current < 800) return;
    lastSilentRefetch.current = now;

    let alive = true;
    (async () => {
      setSilentRefreshing(true);
      await loadPerfume({ withSpinner: false });
      if (alive) setSilentRefreshing(false);
    })();

    return () => { alive = false; };
  }, [version]);

  useLayoutEffect(() => {
    try { if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual"; } catch {}
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    const t = setTimeout(() => { window.scrollTo({ top: 0, left: 0, behavior: "auto" }); }, 0);
    return () => clearTimeout(t);
  }, [product_id]);

  /* ---------- helpers ---------- */

  const isInsufficientFunds = (data = {}) => {
  const err  = String(data?.error || data?.reason || "").toLowerCase();
  const code = String(data?.error_code || data?.response_code || "").toLowerCase();
  const desc = String(data?.response_description || data?.error_message || data?.message || "").toLowerCase();

  const hay = `${err} ${code} ${desc}`;

  // ---- heuristics (works even if gateway uses different codes) ----
  return (
    err === "recurring_failed" && (
      hay.includes("insufficient") ||
      hay.includes("not enough") ||
      hay.includes("funds") ||
      hay.includes("balance") ||
      hay.includes("no money") ||
      hay.includes("51") ||        // common "insufficient funds" code in some gateways
      hay.includes("insuf") ||
      hay.includes("saldo")
    )
  ) || (
    // sometimes backend may directly send insufficient_funds
    hay.includes("insufficient_funds") ||
    hay.includes("not_enough_funds")
  );
};

const paymentFailText = (data = {}, language = "ka") => {
  if (isInsufficientFunds(data)) {
    return language === "en"
      ? "Your card balance is insufficient. Please use another card or top up and try again."
      : "ბარათზე თანხა არ არის საკმარისი. სცადეთ სხვა ბარათი ან შეავსეთ ბალანსი და ხელახლა სცადეთ.";
  }

  const msg =
    data?.response_description ||
    data?.error_message ||
    data?.error ||
    data?.reason ||
    "";

  return language === "en"
    ? (msg ? `Payment failed: ${msg}` : "Payment failed. Please try again.")
    : (msg ? `გადახდა ვერ შესრულდა: ${msg}` : "გადახდა ვერ შესრულდა. სცადეთ ხელახლა.");
};

// === ML helpers (strategy-aware) ===
const mlStrategy = (ml) => String(ml?.stock_strategy || '').toLowerCase();
const isUnitsStrategyMl = (ml) => mlStrategy(ml) === 'units';
const isFullStrategyMl  = (ml) => mlStrategy(ml) === 'full';

  // Accessory variant helpers
const variantHasSale = (v = {}) => {
  const price = Number(v.price ?? 0);
  const sale  = Number(v.sale_p ?? 0);
  return sale > 0 && sale < price;
};
const variantEffectivePrice = (v = {}) => {
  const price = Number(v.price ?? 0);
  const sale  = Number(v.sale_p ?? 0);
  return (sale > 0 && sale < price) ? sale : price;
};


  // Auto-select first color & viable variant for accessories on first load / product change
useEffect(() => {
  if (!perfume || perfume.category !== "accessories") return;
  const variants = perfume.accessory_variants || [];
  if (!variants.length) return;

  const first = variants[0];
  const ka = first?.color_ka ?? first?.color ?? "";
  const en = first?.color_en ?? "";
  const display = (language === "ka" ? (ka || en) : (en || ka)) || "";

  setSelectedColor(prev => prev ?? display);

  const matchesFirstColor = (v) =>
    ((ka && (v.color_ka === ka || v.color === ka)) || (en && v.color_en === en));

  const preferred =
    variants.find(v => matchesFirstColor(v) && Number(v.is_active) === 1 && Number(v.stock) > 0) ||
    variants.find(v => matchesFirstColor(v)) ||
    first;

  setSelectedVariant(prev => (prev ?? preferred) || null); // ✅ ფიქსი
  setQuantity(1);
}, [perfume?.product_id, perfume?.category, language]);


// Keep selectedColor label in sync with language for the currently selected variant
useEffect(() => {
  if (perfume?.category !== "accessories") return;
  if (!selectedVariant) return;

  const ka = selectedVariant.color_ka ?? selectedVariant.color ?? "";
  const en = selectedVariant.color_en ?? "";
  const display = (language === "ka" ? (ka || en) : (en || ka)) || "";

  setSelectedColor(display);
}, [language, perfume?.category, selectedVariant]);

  const getMlRow = (mlSize) => {
    if (!perfume) return null;
    return (perfume.milliliters || []).find(
      (m) => String(m.size) === String(mlSize)
    );
  };

function getMaxQuantityFor(mlSize){
  if (!perfume) return 0;
  const ml = getMlRow(mlSize);
  if (!ml) return 0;

  // per-ML კატეგორიები (atomizers/skin-care/hair-and-body-care): უცვლელი
  if (isPerMlCategory) {
    const perMlStock = Number(ml.unit_stock ?? ml.stock ?? 0);
    return Math.max(0, perMlStock);
  }

  // perfume კატეგორია — ვნახოთ კონკრეტული ML-ის სტრატეგია
  if ((perfume.category || '') === 'perfume') {
    if (isUnitsStrategyMl(ml)) {
      const perMlStock = Number(ml.unit_stock ?? ml.stock ?? 0);
      return Math.max(0, perMlStock);
    }
    // 'full' სტრატეგიის ML — აუზიდან
    const usedByOthersFull = Object.entries(quantities).reduce((sum,[sizeStr,count])=>{
      const s = parseFloat(sizeStr);
      if (s === parseFloat(mlSize)) return sum;
      // აიღე მხოლოდ ის ML-ები, რომლებიც 'full'-ები არიან
      const otherMl = getMlRow(s);
      if (!otherMl || !isFullStrategyMl(otherMl)) return sum;
      return sum + s * (count || 0);
    },0);

    const totalMlPool = Number(perfume.full_stock || 0); // ML
    const remainMl = Math.max(0, totalMlPool - usedByOthersFull);
    return Math.floor(remainMl / parseFloat(mlSize));
  }

  // სხვა კატეგორიები (უსაფრთხო fallback)
  return 0;
}

const mlAvailableText = (ml) => {
  const selCount = Number(quantities[ml.size] || 0);

  // per-ML კატეგორიები (უცვლელად)
  if (isPerMlCategory) {
    const perMlStock = Number(ml.unit_stock ?? ml.stock ?? 0);
    const left = Math.max(0, perMlStock - selCount);
    const txt = language==="en" ? "In stock" : "მარაგი";
    return `${txt}: ${left + selCount} ც`;
  }

  // perfume — ორი模式
  if ((perfume.category || '') === 'perfume') {
    if (isUnitsStrategyMl(ml)) {
      const perMlStock = Number(ml.unit_stock ?? ml.stock ?? 0);
      const left = Math.max(0, perMlStock - selCount);
      const txt = language==="en" ? "In stock" : "მარაგი";
      return `${txt}: ${left + selCount} ც`;
    }
    const maxUnits = getMaxQuantityFor(ml.size);
    const txt = language==="en" ? "Available" : "შესაძლებელია";
    return `${txt}: ${maxUnits} ც`;
  }

  return "";
};

  /* ---------- derived flags (declare BEFORE galleryImages!) ---------- */
  const isMlBased = perfume && ["perfume","skin-care","hair-and-body-care","atomizers"].includes(perfume.category);
  const isPerMlCategory = !!perfume && ["atomizers","skin-care","hair-and-body-care"].includes(perfume.category);

  // open ML block by default for atomizers & care families
  useEffect(() => {
    if (!perfume) return;
    if (["atomizers","skin-care","hair-and-body-care"].includes(perfume.category)) {
      setIsMillilitersOpen(true);
    }
  }, [perfume?.product_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first color & viable variant for accessories on first load
  useEffect(() => {
    if (!perfume || perfume.category !== "accessories") return;
    const variants = perfume.accessory_variants || [];
    if (!variants.length) return;

    const firstColor = variants[0]?.color_ka;
    if (!firstColor) return;

    setSelectedColor((prev) => prev ?? firstColor);

    const preferred =
      variants.find(v => v.color_ka === firstColor && Number(v.is_active) === 1 && Number(v.stock) > 0) ||
      variants.find(v => v.color_ka === firstColor) ||
      variants[0];

    setSelectedVariant((prev) => (prev ?? preferred) || null);
    setQuantity(1);
  }, [perfume]);


  /* ---------- gallery (depends on isMlBased) ---------- */
/* ---------- gallery (depends on isMlBased) ---------- */
const galleryImages = (() => {
  // accessories — selected variant images
  if (perfume?.category === "accessories" && selectedVariant?.images?.length) {
    return selectedVariant.images;
  }

  // ML-based
  if (isMlBased) {
    // მოვლა/ატომაიზერები — COMBINE ყველა ML-ის სურათი (რიგით), არ აქვს მნიშვნელობა რამეა არჩეული
    if (["atomizers", "skin-care", "hair-and-body-care"].includes(perfume.category)) {
      const combined = [];
      (perfume.milliliters || []).forEach((ml) => {
        if (Array.isArray(ml.images)) {
          ml.images.forEach((u) => {
            if (u && !combined.includes(u)) combined.push(u);
          });
        }
      });
      if (combined.length) return combined;
    } else {
      // perfumes — თუ არჩეულია ML, ვაჩვენოთ პირველი არჩეულის სურათები, თორემ fallback
      if (selectedMilliliters.length >= 1) {
        const ml = selectedMilliliters[0];
        if (Array.isArray(ml.images) && ml.images.length) return ml.images;
      }
    }
  }

  // fallback — product images
  return perfume?.imageUrls || [];
})();

  // keep selectedImage in sync with current gallery
  useEffect(() => {
    const imgs = galleryImages;
    if (imgs?.length) {
      if (!imgs.includes(selectedImage)) setSelectedImage(imgs[0]);
    } else {
      setSelectedImage("/placeholder.jpg");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryImages]);

  /* ---------- more derived ---------- */
  const hasAnySelectedMlQty = isMlBased
    ? selectedMilliliters.some(ml => (quantities[ml.size] || 0) > 0)
    : false;

  const canPurchaseAccessories = perfume?.category === "accessories"
    && selectedVariant
    && quantity >= 1
    && Number(selectedVariant?.stock || 0) >= quantity;

  const minMlSize = isMlBased && Array.isArray(perfume?.milliliters) && perfume.milliliters.length
    ? Math.min(...perfume.milliliters.map(m => parseFloat(m.size)))
    : 0;

  const anyUnitsStrategy = isMlBased
    ? (perfume?.milliliters || []).some(m => (m.stock_strategy || "").toLowerCase() === "units")
    : false;

const hasStockForMinMl = isMlBased
  ? (
      isPerMlCategory
        ? (perfume?.milliliters || []).some(m => Number(m.unit_stock ?? m.stock ?? 0) > 0)
        : (minMlSize > 0 && getMaxQuantityFor(minMlSize) >= 1)
    )
  : (Number(perfume?.full_stock || 0) > 0);

  // stock number (used only for accessories UI logic now)
  const stockNumber = perfume?.category === "accessories"
    ? Number(selectedVariant?.stock ?? perfume?.full_stock ?? 0)
    : 0;

  /* ---------- totals ---------- */
  useEffect(()=>{
    if (!perfume) return;
    let total = 0;

    if (isMlBased){
      selectedMilliliters.forEach((ml)=>{
        const price = (ml.sale_price && parseFloat(ml.sale_price) < parseFloat(ml.price))
          ? parseFloat(ml.sale_price)
          : parseFloat(ml.price);
        total += price * (quantities[ml.size] || 0);
      });
    } else if (perfume.category === "accessories"){
      const unitPrice = selectedVariant ? variantEffectivePrice(selectedVariant) : 0;
      total = unitPrice * (quantity || 0);
    }

    setTotalPrice(total.toFixed(2));
  }, [perfume, selectedMilliliters, quantities, quantity, isMlBased, selectedVariant]);

  /* ---------- qty controls ---------- */
  const incrementQuantityMl = (mlSize) => {
    setQuantities(prev=>{
      const cur = prev[mlSize] || 0;
      const max = getMaxQuantityFor(mlSize);
      if (cur < max) return { ...prev, [mlSize]: cur + 1 };
      return prev;
    });
  };
  const decrementQuantityMl = (mlSize) => {
    setQuantities(prev=>{
      const cur = prev[mlSize] || 0;
      if (cur > 0) return { ...prev, [mlSize]: cur - 1 };
      return prev;
    });
  };
  const handleQtyInput = (mlSize, value) => {
    let v = parseInt(value,10) || 0;
    v = Math.min(v, getMaxQuantityFor(mlSize));
    setQuantities({ ...quantities, [mlSize]: v });
  };

  const incAccessory = () => { if (selectedVariant && quantity < Number(selectedVariant.stock || 0)) setQuantity(q => q + 1); };
  const decAccessory = () => { if (quantity > 1) setQuantity(q => q - 1); };

  // prevent removing last ML (ყოველთვის ერთი მაინც უნდა იყოს)
const handleMillilitersToggle = (ml, checked) => {
  if (checked){
    if (getMaxQuantityFor(ml.size) >= 1){
      setSelectedMilliliters(prev => [...prev, ml]);
      setQuantities(prev => ({ ...prev, [ml.size]: Math.max(1, prev[ml.size] || 0) }));
    }
  } else {
    setSelectedMilliliters(prev => prev.filter(x=>String(x.size) !== String(ml.size)));
    setQuantities(prevQ => {
      const u = { ...prevQ }; delete u[ml.size]; return u;
    });
  }
};

  /* ---------- auth / buy now ---------- */
  async function fetchMe(){
    try{
      const res = await api.get(`${API_BASE_URL}/me.php`, { withCredentials:true });
      return res.data;
    }catch{ return { auth:false }; }
  }


  async function openAddressPopup(){
    const me = await fetchMe();
    setPopupAssumeHasPhone(!!me?.has_phone);
    setShowAddressPopup(true);
  }

// --- helper: safe number
const n = (v, def = 0) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : def;
};

// ცოტათი მკაცრი ვალიდაცია — გადავცეთ მხოლოდ რეალური ჩანაწერები
const validateSelection = () => {
  if (!perfume) return false;

  if (isMlBased) {
    // აირჩეული ML-ებიდან დავტოვოთ მხოლოდ ისინი, რომლებსაც რაოდენობა > 0 აქვთ
    const picked = (selectedMilliliters || []).filter(ml => n(quantities[ml.size]) > 0);
    if (!picked.length) {
      setIsMillilitersOpen(true);
      requestAnimationFrame(()=> millilitersRef.current?.scrollIntoView({behavior:"smooth", block:"center"}));
      alert(t("errors.selectMl"));
      return false;
    }
    return true;
  }

  
  if (perfume.category === "accessories") {
    if (!selectedVariant) {
      alert(t("errors.selectVariant") || "აირჩიეთ ფერი/ზომა.");
      return false;
    }
    if (!quantity || quantity < 1) {
      alert(t("errors.accessoryQty"));
      return false;
    }
    return true;
  }

  return false;
};

// --- ახალი handleBuyNow (ჩანაცვლება სრულად)
const handleBuyNow = async (addressData) => {
  const me = await fetchMe();
  const buyerId = (me?.auth && (me.user?.id || me.guest_user_id || me.user_id || me.user?.user_id)) || tempUser?.id;

  if (!buyerId) {
    setShowRegisterModal(true);
    return false;
  }

  // items გენერაცია
  let items = [];
  if (isMlBased) {
    const picked = (selectedMilliliters || []).filter(ml => n(quantities[ml.size]) > 0);
    items = picked.map(ml => {
      const base = n(ml.price);
      const sale = n(ml.sale_price);
      const unit = (sale > 0 && sale < base) ? sale : base;
      return {
        product_id: String(perfume.product_id),
        product_type: "perfume",
        size: n(ml.size),
        quantity: n(quantities[ml.size]),
        price: unit,                    // საკალკულაციოსთვის
        expected_unit_price: unit       // check_stock-სთვის
      };
    });
  } else if (perfume.category === "accessories" && selectedVariant && quantity > 0) {
    const unit = variantEffectivePrice(selectedVariant);
    items = [{
      product_id: String(perfume.product_id),
      product_type: "accessory",
      accessory_variant_id: selectedVariant?.id ?? null,
      color_ka: selectedVariant?.color_ka ?? null,
      size_label: selectedVariant?.size_label ?? null,
      quantity: n(quantity, 1),
      price: n(unit),
      expected_unit_price: n(unit)
    }];
  }

  // დაცვა: ცარიელი ან 0 რაოდენობის items არ გავუშვათ
  items = items.filter(i => n(i.quantity) > 0);
  if (!items.length) {
    alert(t("errors.selectMl") || "აირჩიეთ რაოდენობა.");
    return false;
  }

  const total = items.reduce((s, i) => s + n(i.price) * n(i.quantity), 0);

  const payment_method_token = String(addressData?.payment_method_token || "");
  const save_card = Number(addressData?.save_card || 0);

  const finalTotal = Number((total + deliveryFee).toFixed(2));

  const payload = {
    user_id: buyerId,
    product_id: String(perfume.product_id),
    items,
    purchase_date: Date.now(),                   // Unix ms (ხშირად უფრო მარტივია backend-ზე)
    address: addressData?.address || "",
    city_village: addressData?.city_village || addressData?.city || "",
    comment: addressData?.comment || "",
    payment_method_token,
    save_card,
    language
  };

  // what we validate server-side
  const validationItems = items.map(i => ({
    product_id: i.product_id,
    product_type: i.product_type,
    quantity: i.quantity,
    size: n(i.size, 0),
    expected_unit_price: i.expected_unit_price,
    accessory_variant_id: i.accessory_variant_id ?? null
  }));

  try {
    // 1) STOCK/COST VALIDATION
    const stockRes = await api.post(`${API_BASE_URL}/check_stock.php`, { items: validationItems }, {
      headers: { "Content-Type":"application/json" }
    });

    if (!stockRes?.data?.success) {
      const err = stockRes?.data?.error || "";
      if (err === "price_mismatch") {
        setShowAddressPopup(false);
        setShowPriceModal(true);
        await loadPerfume({ withSpinner: false });
        return false;
      }
      // გამოაჩინე დიფერენცირებული მესიჯი
      openStockModal(
        err
          ? (language==="en" ? `Stock check failed: ${err}` : `მარაგის შემოწმება ჩავარდა: ${err}`)
          : (language==="en" ? "Out of stock or insufficient quantity." : "მარაგი აღარ არის ან საკმარისი აღარ არის.")
      );
      return false;
    }

    // 2) START PAYMENT
    const pay = await api.post(`${API_BASE_URL}/start_payment.php`, payload, {
      headers: { "Content-Type":"application/json" }
    });

    if (pay?.data?.success && pay.data.redirect_url) {
      setShowAddressPopup(false);
      setPayUrl(pay.data.redirect_url);
      setPayModalOpen(true);
      return true;
    }
    

    if (pay?.data?.success) {
      alert(language==="en" ? "✅ Purchase successful! Thank you for your order." : "✅ შეძენა წარმატებულია! მადლობა თქვენი შეკვეთისთვის.");
      setSelectedMilliliters([]); setQuantities({}); setTotalPrice("0.00"); setIsMillilitersOpen(false); setQuantity(1);
      setTimeout(()=>{ loadPerfume({ withSpinner: false }); }, 250);
      return true;
    }

    // --- დეტალური მიზეზები UI-ში ---
    const reason = pay?.data?.reason || "";
    const data = pay?.data || {};
    const err = String(data?.error || "");
    if (reason === "price_changed") {
      setShowAddressPopup(false);
      setShowPriceModal(true);
      await loadPerfume({ withSpinner: false });
      return false;
    }
    if (err === "Unauthorized request!") {
      setShowAuthModal(true);
      return false;
    }
    if (/stock/i.test(err)) {
      openStockModal(language==="en" ? "The item is out of stock or insufficient." : "მარაგი აღარ არის ან საკმარისი აღარ არის.");
      return false;
    }

    if (err === "recurring_failed" || isInsufficientFunds(data)) {
  openStockModal(paymentFailText(data, language)); // სპეციალური ტექსტით
  return false;
}



    // fallback — რეალური ტექსტით (რომ აღარ დაიკარგოს მიზეზი)
    openStockModal(
      (err || reason)
        ? (language==="en" ? `Payment failed: ${err || reason}` : `გადახდა ვერ შესრულდა: ${err || reason}`)
        : (language==="en" ? "An unknown error occurred during payment." : "გადახდისას წარმოიშვა უცნობი შეცდომა.")
    );
    return false;

  } catch (e) {
    // ქსელური ან JSON შეცდომა
    console.error("handleBuyNow error", e);
    openStockModal(language==="en" ? "Network error. Please try again." : "ქსელურ შეცდომა. სცადეთ ხელახლა.");
    return false;
  }
};


  /* ---------- doc title ---------- */
  useEffect(()=>{
    if (!perfume){
      document.title = language==="ka" ? "პროდუქტი | Shopaholics" : "Product | Shopaholics";
      return;
    }
    const nameKey = typeof t === "function" ? t("detailed.name") : null;
    const localized = (nameKey && perfume?.[nameKey]) || perfume?.name || perfume?.name_en || perfume?.name_ka || (language==="ka"?"პროდუქტი":"Product");
    document.title = `${localized} | Shopaholics`;
  },[perfume, t, language]);

  if (loading) return <Loader><Spinner/></Loader>;
  if (!perfume) return null;

const anySale =
  // ML-based sale?
  (Array.isArray(perfume.milliliters) &&
    perfume.milliliters.some(ml => Number(ml.sale_price) > 0 && Number(ml.sale_price) < Number(ml.price))) ||
  // product-level (თუ გაქვს) 
  (perfume.sale_p && Number(perfume.sale_p) > 0 && Number(perfume.sale_p) < Number(perfume.price_p)) ||
  // accessories: variant-level sale_p
  (perfume.category === "accessories" &&
    Array.isArray(perfume.accessory_variants) &&
    perfume.accessory_variants.some(variantHasSale));

    
  return (
    <>
      <Page>
        <TitleMobile>{perfume.name_en ? perfume[t("detailed.name")] : perfume.name}</TitleMobile>

        <Grid>
          {/* LEFT: GALLERY */}
          <GalleryCard>
            <SaleRow>
              <div/>
              {anySale && <SaleBadge>{t("newproducts.sale")}</SaleBadge>}
            </SaleRow>

            <MainImageWrap>
              <MainImage src={selectedImage} alt={perfume.name} />
            </MainImageWrap>

            <Thumbs>
              {galleryImages?.map((url, i)=>(
                <img
                  key={i}
                  src={url}
                  alt={`${perfume.name} ${i+1}`}
                  onClick={()=>setSelectedImage(url)}
                  className={selectedImage===url ? "selected" : ""}
                />
              ))}
            </Thumbs>
          </GalleryCard>

          {/* RIGHT: DETAILS + ACTIONS */}
          <div>
            <DetailsCard>
              <TitleDesktop>{perfume.name_en ? perfume[t("detailed.name")] : perfume.name}</TitleDesktop>

              <Row><strong>{t("detailed.sex")}:</strong>
                <span>
                  {perfume.sex==="woman" ? t("category.woman")
                    : perfume.sex==="man" ? t("category.man")
                    : perfume.sex==="unisex" ? t("category.unisex")
                    : perfume.sex==="kids" ? t("category.kids")
                    : perfume.sex}
                </span>
              </Row>

              <Row><strong>{t("detailed.category")}:</strong>
                <span>
                  {perfume.category==="perfume" ? t("category.perfume")
                    : perfume.category==="skin-care" ? t("category.skinCare")
                    : perfume.category==="hair-and-body-care" ? t("category.hairAndBodyCare")
                    : perfume.category==="atomizers" ? t("category.atomizers")
                    : perfume.category==="accessories" ? t("category.accessories")
                    : perfume.category}
                </span>
              </Row>

              {/* ზემოთ საერთო მარაგის რიგი ML-სთვის აღარ ჩანს */}
              {perfume.category !== "accessories" && !isMlBased && (
                <Row>
                  <strong>{t("detailed.full_stock")}:</strong>
                  <span>{Number(perfume?.full_stock || 0) > 0 ? String(perfume.full_stock) : (language==="en"?"Out of stock":"მარაგში არ არის")}</span>
                </Row>
              )}

              {perfume[t("detailtranslate.extract")] && (
                <Info><strong>{t("detailed.extract")}:</strong> {perfume[t("detailtranslate.extract")]}</Info>
              )}
              <Info><strong>{t("detailed.brand")}:</strong> {perfume.brand}</Info>

              {perfume[t("detailtranslate.ingredients")] && (
                <Info><strong>{t("detailed.ingredients")}:</strong> {perfume[t("detailtranslate.ingredients")]}</Info>
              )}
              {perfume[t("detailtranslate.methodofuse")] && (
                <Info><strong>{t("detailed.methodofuse")}:</strong> {perfume[t("detailtranslate.methodofuse")]}</Info>
              )}
              {perfume[t("detailtranslate.purposeproblem")] && (
                <Info><strong>{t("detailed.purposeproblem")}:</strong> {perfume[t("detailtranslate.purposeproblem")]}</Info>
              )}
              {perfume[t("detailtranslate.producttype")] && (
                <Info><strong>{t("detailed.producttype")}:</strong> {perfume[t("detailtranslate.producttype")]}</Info>
              )}
              {perfume[t("detailtranslate.skintype")] && (
                <Info><strong>{t("detailed.skintype")}:</strong> {perfume[t("detailtranslate.skintype")]}</Info>
              )}
              {perfume[t("detailtranslate.use")] && (
                <Info><strong>{t("detailed.use")}:</strong> {perfume[t("detailtranslate.use")]}</Info>
              )}
              {perfume[t("detailtranslate.material")] && (
                <Info><strong>{t("detailed.material")}:</strong> {perfume[t("detailtranslate.material")]}</Info>
              )}
              {perfume[t("detailtranslate.color")] && (
                <Info><strong>{t("detailed.color")}:</strong> {perfume[t("detailtranslate.color")]}</Info>
              )}

              {/* ACCESSORIES: color/size picker + variant images */}
              {perfume.category === "accessories" && (
                <div style={{ marginTop: 8, border: "1px dashed rgba(0,0,0,.08)", borderRadius: 12, padding: 10 }}>
                  <div style={{ fontWeight: 900, marginBottom: 6 }}>{t("detailed.selectColor") || "ფერის არჩევა"}</div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                    {(() => {
                      const variants = perfume.accessory_variants || [];

                      // ავაგოთ უნიკალური ფერის ლეიბლები KA/EN-ით (ერთად, დუბლიკატების გარეშე)
                      const uniqueColorLabels = [];
                      const seen = new Set();
                      for (const v of variants) {
                        const ka = v.color_ka ?? v.color ?? "";
                        const en = v.color_en ?? "";
                        // ერთი ჩანაწერი თითო ფერზე, ორივე ენის ველებით
                        const key = `${ka}__${en}`;
                        if (!seen.has(key) && (ka || en)) {
                          seen.add(key);
                          uniqueColorLabels.push({ ka, en });
                        }
                      }

                      // helper: ამოირჩიე შესაბამისი ვარიაცია (ჯერ ინ-სტოკ აქტიური)
                      const pickVariantByColor = (ka, en) =>
                        variants.find(x =>
                          ( (ka && (x.color_ka === ka || x.color === ka)) || (en && x.color_en === en) ) &&
                          Number(x.stock) > 0 && Number(x.is_active) === 1
                        ) ||
                        variants.find(x =>
                          ( (ka && (x.color_ka === ka || x.color === ka)) || (en && x.color_en === en) )
                        ) || null;

                      return uniqueColorLabels.map(({ ka, en }) => {
                        // ენის მიხედვით საჩვენებელი ლეიბლი (ფოლბექით)
                        const displayColor = (language === "ka" ? (ka || en) : (en || ka)) || "";

                        // შევადაროთ select-საც ამzelfde ტექსტით
                        const isActive = selectedVariant
                          ? (
                              (ka && ((selectedVariant.color_ka === ka) || (selectedVariant.color === ka))) ||
                              (en && (selectedVariant.color_en === en))
                            )
                          : (selectedColor === displayColor);

                        return (
                          <ColorChip
                            key={`${ka}_${en}`}
onClick={() => {
  const v =
    variants.find(x =>
      ((ka && (x.color_ka === ka || x.color === ka)) || (en && x.color_en === en)) &&
      Number(x.stock) > 0 && Number(x.is_active) === 1
    ) ||
    variants.find(x =>
      (ka && (x.color_ka === ka || x.color === ka)) || (en && x.color_en === en)
    ) || null;

  setSelectedColor(displayColor); // მხოლოდ label-სთვის
  setSelectedVariant(v);
  setQuantity(1);
}}
                              data-active={isActive ? "true" : undefined}
                          data-sale={(() => {
                            // ამ ფერზე რაიმე ვარიანტი თუა სეილზე?
                            const variants = perfume.accessory_variants || [];
                            return variants.some(v =>
                              ((ka && (v.color_ka === ka || v.color === ka)) || (en && v.color_en === en)) &&
                              variantHasSale(v)
                            ) ? "true" : undefined;
                          })()}
                        >
                        {displayColor}

                          </ColorChip>
                        );
                      });
                    })()}

                  </div>

                  {selectedColor && (
                    <>
                      <div style={{ fontWeight: 900, margin: "8px 0 6px" }}>{t("detailed.selectSize") || "ზომის არჩევა"}</div>
                      <SizeButtonPulsePatch>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {(perfume.accessory_variants || [])
    .filter(v => {
      const sv = selectedVariant;
      if (sv) {
        return (
          Number(v.is_active) === 1 &&
          (
            (sv.color_ka && (v.color_ka === sv.color_ka || v.color === sv.color_ka)) ||
            (sv.color_en && v.color_en === sv.color_en)
          )
        );
      }
      // fallback: თუ selectedVariant დროებით არაა, შევამოწმოთ label-ით
      return (
        Number(v.is_active) === 1 &&
        (v.color_ka === selectedColor || v.color === selectedColor || v.color_en === selectedColor)
      );
    })
    .map(v => {
                            const on = selectedVariant?.id === v.id;
                            const out = Number(v.stock) <= 0;
                            return (
<button
  key={v.id}
  onClick={() => { setSelectedVariant(v); setQuantity(1); }}
  disabled={out}
  style={{
    padding: "8px 12px",
    borderRadius: 10,
    border: "1px solid rgba(0,0,0,.12)",
    background: on ? "#111827" : "#fff",
    color: on ? "#fff" : "#111827",
    fontWeight: 900,
    opacity: out ? 0.5 : 1,
    cursor: out ? "not-allowed" : "pointer",
  }}
  data-sale={variantHasSale(v) ? "true" : undefined}
>
  {v.size_label} ·{" "}
  {variantHasSale(v) ? (
    <>
      <span style={{ textDecoration: "line-through", color: "#9ca3af" }}>
        {Number(v.price).toFixed(2)} {t("detailed.currency")}
      </span>{" "}
      <span>
        {variantEffectivePrice(v).toFixed(2)} {t("detailed.currency")}
      </span>
    </>
  ) : (
    <span>
      {Number(v.price).toFixed(2)} {t("detailed.currency")}
    </span>
  )}
  {out && <span style={{ marginLeft: 6, color: "#DC2626", fontWeight: 800 }}>({t("outOfStock") || "მარაგი არაა"})</span>}
</button>
                            );
                          })}
                      </div>
                      </SizeButtonPulsePatch>

                      {selectedVariant?.images?.length === 0 && (
                        <div style={{ marginTop: 6, color: "#6b7280", fontSize: 13 }}>
                          {t("detailed.noImagesForSelection") || "ამ არჩევანზე სურათები არ არის. ნაჩვენებია პროდუქტის ძირითადი სურათები."}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {perfume.aroma_en && <AromaSection perfume={perfume} />}

              {/* ML-based selector */}
              {isMlBased && (
                <MlBlock>
                  <Toggle onClick={()=> setIsMillilitersOpen(o=>!o)} ref={millilitersRef} aria-expanded={isMillilitersOpen}>
                    <span><strong>{t("detailed.mililitersandstock")}</strong></span>
                    <span>{isMillilitersOpen ? "−" : "+"}</span>
                  </Toggle>

                  <ToggleContent open={isMillilitersOpen}>
                    {[...perfume.milliliters]
                      .sort((a,b)=>parseFloat(a.size)-parseFloat(b.size))
                      .map((ml, idx)=>{
                      const disabled = getMaxQuantityFor(ml.size) < 1 && !selectedMilliliters.some(x=>String(x.size)===String(ml.size));
                        const onSale = ml.sale_price && parseFloat(ml.sale_price) < parseFloat(ml.price);
                        return (
                          <div key={idx} style={{padding:"6px 2px"}}>
                            {onSale ? (
                              <>
                                <PriceWrap><span className="old">{ml.size}{t("detailed.ml")} – {ml.price}{t("detailed.currency")}</span></PriceWrap>
                                <MlRow disabled={disabled} data-sale={onSale ? "true" : undefined}>
                                  <input
                                    type="checkbox"
                                    disabled={disabled}
                                    checked={selectedMilliliters.some(s=>String(s.size)===String(ml.size))}
                                    onChange={(e)=>handleMillilitersToggle(ml, e.target.checked)}
                                  />
                                  <span className="chk" />
                                  <span><strong>{ml.size}{t("detailed.ml")} – {ml.sale_price}{t("detailed.currency")}</strong></span>
                                </MlRow>
                              </>
                            ) : (
                              <MlRow disabled={disabled} data-sale={onSale ? "true" : undefined}>
                                <input
                                  type="checkbox"
                                  disabled={disabled}
                                  checked={selectedMilliliters.some(s=>String(s.size)===String(ml.size))}
                                  onChange={(e)=>handleMillilitersToggle(ml, e.target.checked)}
                                />
                                <span className="chk" />
                                <span><strong>{ml.size}{t("detailed.ml")} – {ml.price}{t("detailed.currency")}</strong></span>
                              </MlRow>
                            )}
                            {/* per-ML stock just under the row */}
                            <MlStock>{mlAvailableText(ml)}</MlStock>
                          </div>
                        );
                      })}
                  </ToggleContent>
                </MlBlock>
              )}

              {/* DESCRIPTION */}
              {perfume[t("detailtranslate.description")] && (
                <>
                  <Toggle onClick={()=>setIsDescriptionOpen(o=>!o)} ref={descriptionRef} aria-expanded={isDescriptionOpen}>
                    <span><strong>{t("detailed.description")}</strong></span>
                    <span>{isDescriptionOpen ? "−" : "+"}</span>
                  </Toggle>
                  <ToggleContent open={isDescriptionOpen}><p>{perfume[t("detailtranslate.description")]}</p></ToggleContent>
                </>
              )}

              {/* NOTE */}
              {perfume[t("detailtranslate.note")] && (
                <>
                  <Toggle onClick={()=>setIsNoteOpen(o=>!o)} ref={noteRef} aria-expanded={isNoteOpen}>
                    <span><strong>{t("detailed.note")}</strong></span>
                    <span>{isNoteOpen ? "−" : "+"}</span>
                  </Toggle>
                  <ToggleContent open={isNoteOpen}><p>{perfume[t("detailtranslate.note")]}</p></ToggleContent>
                </>
              )}
            </DetailsCard>

            <ActionsCard>
              {/* Quantities */}
              {isMlBased ? (
                selectedMilliliters.map((ml, i)=>{
                  const onSale = ml.sale_price && parseFloat(ml.sale_price) < parseFloat(ml.price);
                  return (
                    <QtyRow key={i}>
                      <QtyBadge>{ml.size}{t("detailed.ml")}:</QtyBadge>
                      <QtyField>
                        <QtyBtn onClick={()=>decrementQuantityMl(ml.size)} disabled={(quantities[ml.size]||0) <= 0}>−</QtyBtn>
                        <QtyInput
                          type="number" min="0" max={getMaxQuantityFor(ml.size)}
                          value={quantities[ml.size] || 0}
                          onChange={(e)=>handleQtyInput(ml.size, e.target.value)}
                        />
                        <QtyBtn onClick={()=>incrementQuantityMl(ml.size)} disabled={(quantities[ml.size]||0) >= getMaxQuantityFor(ml.size)}>+</QtyBtn>
                      </QtyField>
                      <div style={{marginLeft:"auto", fontWeight:800}}>
                        {onSale ? (<span>{ml.sale_price} {t("detailed.currency")}</span>) : (<span>{ml.price} {t("detailed.currency")}</span>)}
                      </div>
                    </QtyRow>
                  );
                })
              ) : (
                <QtyRow>
                  <QtyBadge>{t("detailed.quantity")}:</QtyBadge>
                  <QtyField>
                    <QtyBtn onClick={decAccessory} disabled={quantity<=1 || !selectedVariant}>−</QtyBtn>
                    <QtyInput type="number" value={quantity} readOnly />
                    <QtyBtn onClick={incAccessory} disabled={!selectedVariant || quantity >= Number(selectedVariant?.stock || 0)}>+</QtyBtn>
                  </QtyField>
                </QtyRow>
              )}

              {/* Total */}
<div style={{
  border: "1px solid rgba(0,0,0,.08)",
  borderRadius: 12,
  padding: 12,
  background: "#fff",
  display: "grid",
  gap: 6
}}>
  <div style={{display:"flex",justifyContent:"space-between"}}>
    <span style={{fontWeight:800}}>
      {language === "ka" ? "პროდუქტების ჯამი" : "Products total"}
    </span>
    <span style={{fontWeight:900}}>
      {totalPrice} {t("detailed.currency")}
    </span>
  </div>

  <div style={{display:"flex",justifyContent:"space-between", color:"#6b7280"}}>
    <span style={{fontWeight:700}}>
      {language === "ka" ? "მიწოდება" : "Delivery"}
    </span>
    <span style={{fontWeight:800}}>
      {deliveryFee.toFixed(2)} {t("detailed.currency")}
    </span>
  </div>

  <hr style={{border:"none",borderTop:"1px solid rgba(0,0,0,.08)"}} />

  <div style={{display:"flex",justifyContent:"space-between"}}>
    <span style={{fontWeight:900}}>
      {language === "ka" ? "სრული ფასი" : "Total price"}
    </span>
    <span style={{fontWeight:900}}>
      {(Number(totalPrice) + deliveryFee).toFixed(2)} {t("detailed.currency")}
    </span>
  </div>
</div>
              {/* Buttons */}
              {!hideActions && (
                <Buttons>
                  <button
                    className="btn primary"
                    onClick={async ()=>{
                      if (!validateSelection()) return;
                      const stored = localStorage.getItem("user");
                      const u = stored ? JSON.parse(stored) : null;
                      if (!u?.id){
                        setShowRegisterModal(true);
                      }else{
                        await openAddressPopup();
                      }
                    }}
                    disabled={ isMlBased ? !hasAnySelectedMlQty : !canPurchaseAccessories }
                  >
                    {t("detailed.buynow")}
                  </button>

                  <div className="or">{t("detailed.or")}</div>

<button
  className="btn secondary"
  onClick={() => {
    if (isMlBased) {
      // ფილტრავ მხოლოდ იმ ML-ებს, რომლებსაც რაოდენობა > 0 აქვთ
      const picked = (selectedMilliliters || []).filter(ml => (quantities[ml.size] || 0) > 0);
      if (picked.length === 0) return; // არ დაამატო არაფერი თუ არაფერი აირჩია

      const payload = {
        ...perfume,
        selectedMilliliters: picked,
        quantities, // CartOverlay მაინც თავისას იზამს
      };
      addToCart(payload);
      return;
    }

    // აქსესუარები — გადავცეთ არჩეული ვარიანტი და რაოდენობა
    if (perfume.category === "accessories" && selectedVariant && quantity > 0) {
        const payload = {
          ...perfume,
          selectedVariant: {
            ...selectedVariant,
            effective_price: variantEffectivePrice(selectedVariant), // <- სასარგებლოა კალათისთვის
          },
          quantity,
        };
        addToCart(payload);
    }
  }}
  disabled={ isMlBased ? !hasAnySelectedMlQty : !(quantity>=1 && stockNumber>=1) }
>
  {t("detailed.addtocart")}
</button>
                </Buttons>
              )}
            </ActionsCard>
          </div>
        </Grid>

        {/* Reviews */}
        <div style={{marginTop:16}}>
          <Review perfumeId={perfume.product_id} userId={userId} />
        </div>
      </Page>

      {/* Modals */}
      <PriceChangedModal show={showPriceModal} onClose={()=>setShowPriceModal(false)} language={language} />

      {stockModalOpen && <StockModal message={stockMessage} onClose={() => setStockModalOpen(false)} />}

      <AuthExpiredModal
        visible={showAuthModal}
        language={language}
        onClose={()=>{
          setShowAuthModal(false);
          localStorage.removeItem("user");
          window.location.href = "/login";
        }}
      />

      {showRegisterModal && (
        <MiniRegisterModal
          onClose={()=>setShowRegisterModal(false)}
          onSubmitted={async (payload)=>{
            try{
              const res = await api.post(
                `${API_BASE_URL}/guest_start.php`,
                { ...payload, lang: language },
                { headers: { "Content-Type":"application/json" }, withCredentials: true }
              );
              const data = res.data;
              if (data?.success){
                setTempUser({
                  id: data.guest_user_id || data.user_id || data?.user?.id,
                  type: "guest",
                  email: payload?.email,
                });
                setShowRegisterModal(false);
                const guessedHasPhone = !!payload?.phone || !!payload?.phone_e164;
                setPopupAssumeHasPhone(guessedHasPhone);
                setShowAddressPopup(true);
              }
              return data;
            }catch(e){
              console.error(e);
              return { success:false, error_code:"NETWORK", error:"Network error" };
            }
          }}
        />
      )}

{payModalOpen && (
  <PaymentIframeModal
    url={payUrl}
    language={language}
    onClose={() => {
      setPayModalOpen(false);
      setPayUrl("");
    }}
  />
)}

      {showAddressPopup && (
        <AddressPopup
          onClose={()=>setShowAddressPopup(false)}
          onSubmit={async (data)=>{
            const ok = await handleBuyNow(data);
            if (ok) setShowAddressPopup(false);
          }}
          assumeHasPhone={popupAssumeHasPhone}
          knownUserId={ userId || tempUser?.id || null }
        />
      )}
    </>
  );
};

export default PerfumeDetail;
